import { useCallback, useEffect, useState } from 'react';
import { useI18n } from '@react/hooks';
import { Alert } from '@react/components';
import { EntityChatPanel } from '@/features/chat/EntityChatPanel';
import { StatusBadge } from '@/components/StatusBadge';
import {
  acceptDocumentSuggestion,
  buildDocumentReviewMetadata,
  buildDocumentReviewStatusRequest,
  buildDocumentSuggestionStatusRequest,
  DOCUMENT_SUGGESTION_REVIEW_KIND,
  documentReviewStatus,
  isDocumentSuggestion,
  reviewAnchorFromMessage,
  scrollToDocumentReviewAnchor,
  type DocumentReviewAnchor,
} from './review';

type ReviewMode = 'comment' | 'suggestion';

export interface DocumentReviewPanelProps {
  documentId: string;
  documentVersion: string | number;
  selectedBlock?: DocumentReviewAnchor | null;
  onAnchorNavigate?: (anchor: DocumentReviewAnchor) => boolean | void;
  canAcceptSuggestions?: boolean;
  onSuggestionAccepted?: () => void;
}

export function DocumentReviewPanel({
  documentId,
  documentVersion,
  selectedBlock,
  onAnchorNavigate,
  canAcceptSuggestions = true,
  onSuggestionAccepted,
}: DocumentReviewPanelProps) {
  const { t } = useI18n();
  const [missingBlockId, setMissingBlockId] = useState<string | null>(null);
  const [mode, setMode] = useState<ReviewMode>('comment');
  const [suggestedText, setSuggestedText] = useState('');

  useEffect(() => {
    setSuggestedText('');
  }, [selectedBlock?.blockId, documentVersion]);

  const composerMetadata = selectedBlock
    ? buildDocumentReviewMetadata({
        documentId,
        documentVersion,
        blockId: selectedBlock.blockId,
        anchorText: selectedBlock.anchorText,
        reviewKind: mode === 'suggestion' ? DOCUMENT_SUGGESTION_REVIEW_KIND : undefined,
        suggestedText,
      })
    : null;

  const messageFilter = useCallback(
    (message: any) => reviewAnchorFromMessage(message, documentId) !== null,
    [documentId],
  );

  const renderAnchor = useCallback((message: any) => {
    const anchor = reviewAnchorFromMessage(message, documentId);
    if (!anchor) return null;
    const status = documentReviewStatus(message);
    const statusKey = status === 'resolved'
      ? 'document.review.statusResolved'
      : status === 'accepted'
        ? 'document.review.statusAccepted'
        : status === 'rejected'
          ? 'document.review.statusRejected'
          : 'document.review.statusOpen';
    const suggestion = isDocumentSuggestion(message)
      && typeof message?.metadata?.suggested_text === 'string'
      ? message.metadata.suggested_text
      : '';
    return (
      <span className="flex min-w-0 flex-col gap-1">
        <span className="flex min-w-0 items-center gap-2">
          <span className="min-w-0 flex-1 truncate">
            {anchor.anchorText ? `“${anchor.anchorText}”` : t('document.review.blockAnchor')}
          </span>
          <StatusBadge
            size="sm"
            variant={status === 'resolved' || status === 'accepted' ? 'success' : status === 'rejected' ? 'secondary' : 'warning'}
            label={t(statusKey)}
          />
          {anchor.documentVersion && (
            <span className="shrink-0 tabular-nums text-muted-foreground">
              {t('document.review.versionAnchor', { version: anchor.documentVersion })}
            </span>
          )}
        </span>
        {suggestion && (
          <span className="whitespace-pre-wrap break-words text-foreground">
            {t('document.review.suggestedTextLabel')}: {suggestion}
          </span>
        )}
      </span>
    );
  }, [documentId, t]);

  const messageActionFor = useCallback((message: any) => {
    if (!reviewAnchorFromMessage(message, documentId)) return null;
    const status = documentReviewStatus(message);
    if (isDocumentSuggestion(message)) {
      if (status !== 'open') return null;
      const reject = {
        label: t('document.review.rejectSuggestion'),
        request: buildDocumentSuggestionStatusRequest(message, 'rejected'),
      };
      if (!canAcceptSuggestions) return reject;
      return [
        {
          label: t('document.review.acceptSuggestion'),
          request: buildDocumentSuggestionStatusRequest(message, 'accepted'),
          beforeRequest: () => acceptDocumentSuggestion(message, { onAccepted: onSuggestionAccepted }),
        },
        reject,
      ];
    }
    return {
      label: t(status === 'resolved' ? 'document.review.reopen' : 'document.review.resolve'),
      request: buildDocumentReviewStatusRequest(message),
    };
  }, [canAcceptSuggestions, documentId, onSuggestionAccepted, t]);

  const handleAnchor = useCallback((message: any) => {
    const anchor = reviewAnchorFromMessage(message, documentId);
    if (!anchor) return;
    setMissingBlockId(null);
    const navigated = onAnchorNavigate
      ? onAnchorNavigate(anchor)
      : scrollToDocumentReviewAnchor(anchor);
    if (navigated === false) setMissingBlockId(anchor.blockId);
  }, [documentId, onAnchorNavigate]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      {missingBlockId && (
        <Alert variant="error">
          <span role="alert">{t('document.review.blockMissing')}</span>
        </Alert>
      )}
      {selectedBlock && (
        <div className="flex gap-0.5 rounded-lg bg-muted/50 p-0.5" role="group" aria-label={t('document.review.modeLabel')}>
          {(['comment', 'suggestion'] as ReviewMode[]).map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={mode === value}
              onClick={() => setMode(value)}
              className={`flex-1 rounded-md px-2 py-1 text-xs transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none ${
                mode === value
                  ? 'bg-accent font-medium text-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground'
              }`}
            >
              {t(value === 'comment' ? 'document.review.modeComment' : 'document.review.modeSuggestion')}
            </button>
          ))}
        </div>
      )}
      {selectedBlock && mode === 'suggestion' && (
        <textarea
          value={suggestedText}
          onChange={(event) => setSuggestedText(event.target.value)}
          rows={3}
          placeholder={t('document.review.suggestedTextPlaceholder')}
          aria-label={t('document.review.suggestedTextLabel')}
          className="w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      )}
      {!selectedBlock ? (
        <p className="text-xs text-muted-foreground">{t('document.review.selectBlock')}</p>
      ) : !composerMetadata && (
        <p className="text-xs text-muted-foreground">{t('document.review.suggestionRequired')}</p>
      )}
      <div className="min-h-0 flex-1">
        <EntityChatPanel
          entity={{ id: documentId, type_name: 'document' }}
          title={t('document.review.threadsTitle')}
          defaultConversationName={t('document.review.conversationName')}
          composerMetadata={composerMetadata}
          composerContentRequired={mode === 'suggestion'}
          messageFilter={messageFilter}
          renderAnchor={renderAnchor}
          onAnchor={handleAnchor}
          messageActionFor={messageActionFor}
          messageActionError={t('document.review.actionError')}
        />
      </div>
    </div>
  );
}

export type { DocumentReviewAnchor } from './review';
export default DocumentReviewPanel;
